import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, fmt, gameUrl } from "../lib/api";
import { useApi, useSeo, useSettings } from "../lib/settings";
import { SITE, clip, crumbs } from "../lib/seo";
import { track, gameParams } from "../lib/analytics";
import { pushPlayed, toggleFav, useList, FAV, getVote, saveVote } from "../lib/store";
import { AdSlot } from "../components/AdSlot";
import { GameItem, useHoverVideo, HoverVideo } from "../components/GameItem";
import { Walkthrough } from "../components/Walkthrough";

function SideItem({ g }) {
  const { show, on, off } = useHoverVideo(g.video);
  return (
    <li>
      <Link to={gameUrl(g.slug)} className="game-item" onMouseEnter={on} onMouseLeave={off} data-testid={`related-${g.slug}`}>
        <img src={g.image} alt={g.title} loading="lazy" />
        {show && <HoverVideo src={g.video} />}
        <p className="post-name" style={{ zIndex: 3 }}>{g.title}</p>
      </Link>
    </li>
  );
}

function Toast({ msg }) {
  if (!msg) return null;
  return <div className="gg-toast" data-testid="vote-toast">{msg}</div>;
}

function Actions({ game, counts, onVote, onFull, category }) {
  const favs = useList(FAV);
  const isFav = favs.includes(game.slug);
  const v = counts.vote;
  const fav = () => {
    if (!isFav) track("add_to_favorites", gameParams(game, category));
    toggleFav(game.slug);
  };
  return (
    <>
      <div className="play-info-blue" data-testid="game-plays"><i className="fa-solid fa-gamepad" /><span>{fmt(counts.plays)} partite</span></div>
      <button className={`vote-btn ${v === "like" ? "on" : ""}`} data-type="like" onClick={() => onVote("like")} data-testid="vote-like-btn">
        <i className={v === "like" ? "fa-solid fa-thumbs-up" : "fa-regular fa-thumbs-up"} /><span>{fmt(counts.likes)}</span>
      </button>
      <button className={`vote-btn ${v === "dislike" ? "on" : ""}`} data-type="dislike" onClick={() => onVote("dislike")} data-testid="vote-dislike-btn">
        <i className={v === "dislike" ? "fa-solid fa-thumbs-down" : "fa-regular fa-thumbs-down"} /><span>{fmt(counts.dislikes)}</span>
      </button>
      <button className={`vote-btn ${isFav ? "on" : ""}`} data-type="favorite" onClick={fav} data-testid="favorite-btn" aria-label="Preferiti">
        <i className={isFav ? "fa-solid fa-heart" : "fa-regular fa-heart"} />
      </button>
      <button className="vote-btn" onClick={onFull} data-testid="fullscreen-btn" aria-label="Schermo intero"><i className="fa-solid fa-expand" /></button>
    </>
  );
}

function Player({ game, playing, onPlay, boxRef, fs, onExitFs }) {
  return (
    <div id="ava-game_container" className={`game-box ${fs ? "gg-fs" : ""}`} ref={boxRef}>
      {fs && (
        <button type="button" className="gg-fs-close" onClick={onExitFs} aria-label="Esci da schermo intero" data-testid="exit-fullscreen-btn">
          <i className="fa-solid fa-xmark" />
        </button>
      )}
      {!playing ? (
        <div id="gamePlay-content" onClick={onPlay} data-testid="play-now-btn">
          <img className="gamePlay-bg" src={game.image} alt={`immagine ${game.title}`} />
          <div className="gamePlay-icon" style={{ backgroundImage: `url(${game.image})`, backgroundSize: "cover", backgroundPosition: "50% 50%" }} />
          <div className="gamePlay-button">Gioca ora!</div>
          <div className="gamePlay-title">{game.title}</div>
        </div>
      ) : (
        <div id="game-box">
          <iframe src={game.file} title={game.title} allow="autoplay; fullscreen; gamepad" allowFullScreen scrolling="no" data-testid="game-iframe" />
        </div>
      )}
    </div>
  );
}

function GameView({ data }) {
  const { game, related = [], tags = [], category } = data;
  const [playing, setPlaying] = useState(false);
  const [counts, setCounts] = useState({ plays: game.plays, likes: game.likes, dislikes: game.dislikes, vote: getVote(game.slug) });
  const [toast, setToast] = useState("");
  const [fakeFs, setFakeFs] = useState(false);
  const box = useRef(null);
  const { ads } = useSettings();
  const path = `/gioco/${game.slug}`;
  const t1 = `${game.title} - Gioco Gratis Online`;
  useSeo({
    title: t1.length <= 60 ? t1 : `${game.title} - Gioca Gratis`,
    description: clip(game.description, 155),
    path,
    image: game.image,
    type: "article",
    jsonld: SITE && [
      {
        "@context": "https://schema.org",
        "@type": "VideoGame",
        name: game.title,
        description: clip(game.description, 300),
        image: game.image,
        url: `${SITE}${path}`,
        genre: category?.heading || category?.name || undefined,
        keywords: tags.map((t) => t.name).join(", ") || undefined,
        gamePlatform: ["Web browser", "PC", "Mobile"],
        applicationCategory: "Game",
        operatingSystem: "Any",
        isAccessibleForFree: true,
      },
      crumbs([["Home", "/"], ...(category ? [[category.heading || category.name, `/categoria/${category.slug}`]] : []), [game.title, path]]),
    ],
  });

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(""), 2000);
    return () => clearTimeout(t);
  }, [toast]);

  const play = () => {
    setPlaying(true);
    pushPlayed(game.slug);
    track("game_start", gameParams(game, category));
    setCounts((c) => ({ ...c, plays: c.plays + 1 }));
    api.post(`/games/${game.slug}/play`).catch(() => {});
  };
  const vote = async (type) => {
    if (counts.vote === type) return setToast("Hai già votato");
    try {
      const r = await api.post(`/games/${game.slug}/vote`, { type, undo: counts.vote });
      saveVote(game.slug, type);
      track(type === "like" ? "game_like" : "game_dislike", gameParams(game, category));
      setCounts((c) => ({ ...c, likes: r.data?.likes ?? c.likes, dislikes: r.data?.dislikes ?? c.dislikes, vote: type }));
      setToast("Grazie per il tuo voto!");
    } catch {
      setToast("Errore, riprova");
    }
  };
  useEffect(() => {
    if (!fakeFs) return undefined;
    document.body.classList.add("gg-fs-lock");
    return () => document.body.classList.remove("gg-fs-lock");
  }, [fakeFs]);

  const full = () => {
    track("game_fullscreen", gameParams(game, category));
    if (!playing) play();
    const el = box.current;
    const req = el?.requestFullscreen || el?.webkitRequestFullscreen;
    // iPhone Safari has no fullscreen API for non-video elements: fall back to a full-viewport overlay
    if (!req) return setFakeFs(true);
    try {
      const p = req.call(el);
      if (p && p.catch) p.catch(() => setFakeFs(true));
      if (window.screen?.orientation?.lock) window.screen.orientation.lock("landscape").catch(() => {});
    } catch {
      setFakeFs(true);
    }
  };
  const left = related.slice(0, 8);
  const right = related.slice(8, 16);
  const bottom = related.slice(16);
  const hasTop = !!ads?.header_728;

  return (
    <div data-testid="game-page">
      {hasTop && <div id="topad" className="fn-clear"><AdSlot name="header_728" className="ad728" /></div>}
      <div id="game-col" className="fn-clear" style={{ marginTop: 10 }}>
        <div className="game-left fn-left">
          <div className="relategames fn-right gg-related-right">
            <div style={{ gridColumn: "1 / -1", marginBottom: 6 }}>Giochi simili</div>
            <ul style={{ display: "contents" }}>{left.map((g) => <SideItem key={g.slug} g={g} />)}</ul>
          </div>
        </div>
        <div className="game-info bgs fn-left">
          <div className="game-info">
            <div id="gameDiv">
              <div className="gametitle fn-clear">
                <h1 className="gamename fn-left" title={game.title} data-testid="game-title"><i className="flag" /><span>{game.title}</span></h1>
                <div className="game-share"><Actions game={game} counts={counts} onVote={vote} onFull={full} category={category} /></div>
              </div>
              <Player game={game} playing={playing} onPlay={play} boxRef={box} fs={fakeFs} onExitFs={() => setFakeFs(false)} />
              <div className="gg-mobile-play"><Actions game={game} counts={counts} onVote={vote} onFull={full} category={category} /></div>
            </div>
          </div>
        </div>
        <div className="game-right fn-left">
          <div className="relategames fn-left gg-related-right">
            <div style={{ gridColumn: "1 / -1", marginBottom: 6 }}>Giochi simili</div>
            <ul style={{ display: "contents" }}>{right.map((g) => <SideItem key={g.slug} g={g} />)}</ul>
          </div>
          <AdSlot name="sidebar_300" className="ad300" />
        </div>
      </div>
      <AdSlot name="game_bottom" />
      <div id="game-bottom" className="bgs fn-clear">
        <div className="game-dec fn-left">
          <div className="thumb">
            <div style={{ backgroundImage: `url(${game.image})`, backgroundSize: "cover", backgroundPosition: "50% 50%", width: "100%", height: "100%", borderRadius: 6 }} />
          </div>
          <div className="description">
            <p className="pl game-title" style={{ fontSize: 25 }}>{game.title}</p>
            {category && <p style={{ marginTop: 18 }}>Categoria: <Link to={`/categoria/${category.slug}`} style={{ color: "#fc0" }} data-testid="game-category-link">{category.name}</Link></p>}
            <p className="d-text" style={{ marginTop: 15, fontSize: 16 }} data-testid="game-description">{game.description}</p>
            {game.instructions && <p className="gg-instr"><i className="fa-solid fa-circle-info" /> <b>Come si gioca:</b> {game.instructions}</p>}
          </div>
        </div>
        {tags.length > 0 && (
          <div className="game-tags fn-right">
            <span className="pl game-title" style={{ fontSize: 25, color: "white", marginBottom: 5 }}>Tag</span>
            <ul>{tags.map((t) => <li key={t.slug}><Link to={`/tag/${t.slug}`}>{t.name}</Link></li>)}</ul>
          </div>
        )}
      </div>
      <Walkthrough game={game} />
      {bottom.length > 0 && (
        <div id="game-bottom" style={{ marginTop: 20 }} className="play-game-bottom flex-similar-games flex-col bgs fn-clear">
          <h2>Giochi simili</h2>
          <div className="gg-sim">{bottom.map((g) => <GameItem key={g.slug} g={g} />)}</div>
        </div>
      )}
      <Toast msg={toast} />
    </div>
  );
}

export default function GamePage() {
  const { slug } = useParams();
  const { data, loading, error } = useApi(`/games/${slug}`, [slug]);
  if (loading && !data) return <div className="gg-empty"><i className="fa-solid fa-spinner fa-spin" /></div>;
  if (error || !data?.game) {
    return <NotFoundGame />;
  }
  return <GameView key={slug} data={data} />;
}

function NotFoundGame() {
  useSeo({ title: "Gioco non trovato", noindex: true });
  return <div className="gg-empty" data-testid="game-not-found">Gioco non trovato. <Link to="/">Torna alla home</Link></div>;
}
